/**
 * Token Storage Utility
 * Handles secure storage and retrieval of JWT tokens
 */

const TOKEN_KEYS = {
    ACCESS_TOKEN: 'authToken', // Keep existing key name for backward compatibility
    REFRESH_TOKEN: 'refreshToken',
    TOKEN_EXPIRES_AT: 'tokenExpiresAt',
};

export interface Tokens {
    access: string;
    refresh: string;
}

/**
 * Store tokens after successful login
 * @param tokens - Object containing access and refresh tokens
 */
export const storeTokens = (tokens: Tokens): void => {
    if (!tokens || !tokens.access || !tokens.refresh) {
        if (process.env.NODE_ENV === 'development') {
            console.error('Invalid tokens provided');
        }
        return;
    }

    try {
        // Store tokens
        localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, tokens.access);
        localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, tokens.refresh);
        
        // Calculate expiration time (1 hour from now)
        const expiresAt = new Date().getTime() + (60 * 60 * 1000); // 1 hour in milliseconds
        localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRES_AT, expiresAt.toString());
    } catch (error) {
        // Handle localStorage quota exceeded or other storage errors
        if (process.env.NODE_ENV === 'development') {
            console.error('Failed to store tokens:', error);
        }
        throw new Error('Failed to store authentication tokens');
    }
};

/**
 * Get access token from storage
 * @returns Access token or null if not found
 */
export const getAccessToken = (): string | null => {
    return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
};

/**
 * Get refresh token from storage
 * @returns Refresh token or null if not found
 */
export const getRefreshToken = (): string | null => {
    return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
};

/**
 * Check if access token is expired or about to expire
 * @param bufferMinutes - Minutes before expiration to consider expired (default: 5)
 * @returns True if token is expired or expiring soon
 */
export const isTokenExpired = (bufferMinutes: number = 5): boolean => {
    const expiresAt = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRES_AT);
    if (!expiresAt) return true;
    
    const expirationTime = parseInt(expiresAt, 10);
    const bufferTime = bufferMinutes * 60 * 1000; // Convert to milliseconds
    const currentTime = new Date().getTime();
    
    return (currentTime + bufferTime) >= expirationTime;
};

/**
 * Clear all tokens from storage
 */
export const clearTokens = (): void => {
    try {
        localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRES_AT);
    } catch (error) {
        // Silently handle localStorage errors during cleanup
        if (process.env.NODE_ENV === 'development') {
            console.error('Error clearing tokens:', error);
        }
    }
};

/**
 * Check if user is authenticated (has valid tokens)
 * @returns True if user has tokens
 */
export const isAuthenticated = (): boolean => {
    return !!getAccessToken() && !!getRefreshToken();
};

/**
 * Update access token (used after refresh)
 * @param accessToken - New access token
 */
export const updateAccessToken = (accessToken: string): void => {
    if (!accessToken) {
        if (process.env.NODE_ENV === 'development') {
            console.error('Invalid access token provided');
        }
        return;
    }

    try {
        localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
        
        // Update expiration time (1 hour from now)
        const expiresAt = new Date().getTime() + (60 * 60 * 1000);
        localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRES_AT, expiresAt.toString());
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.error('Failed to update access token:', error);
        }
        throw new Error('Failed to update authentication token');
    }
};
